/**
 * InstallBanner — banner "Adicionar à tela inicial" (PWA A2HS, Story X1.1.3).
 *
 * Fica montado globalmente (App.tsx) e se auto-gate: só renderiza em viewport
 * mobile, quando o app ainda não está instalado/standalone e o dismiss de 7d
 * não está ativo. A lógica de decisão é pura (lib/pwaInstall.ts); aqui fica só
 * o wire com os eventos do browser.
 *
 * Duas variantes (3 opt × 3, todas recomendadas):
 * - **native** (Chromium Android/desktop): captura `beforeinstallprompt`,
 *   mostra botão "Adicionar à tela inicial" que chama `prompt()`.
 * - **ios** (iOS Safari, que NÃO dispara o evento): instruções manuais
 *   (Compartilhar → "Adicionar à Tela de Início").
 *
 * Tracking sem sink genérico: `appinstalled` grava flag em localStorage
 * (suprime o banner pra sempre) + log `[PWA]`. Dismiss persiste 7 dias.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Share, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  bannerMode,
  ehIos,
  estaInstalado,
  isDismissActive,
  isInstalled,
  markInstalled,
  setDismissed,
  shouldShowBanner,
  type InstallBannerMode,
} from "@/lib/pwaInstall";

/** `beforeinstallprompt` não está nos types padrão do DOM — shim mínimo. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function InstallBanner() {
  const isMobile = useIsMobile();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  // Reavaliação: bump pra recomputar `visible` após eventos/dismiss.
  const [, forceTick] = useState(0);
  const recompute = useCallback(() => forceTick((n) => n + 1), []);

  useEffect(() => {
    if (isInstalled(window.localStorage)) setInstalled(true);

    const onBeforeInstall = (e: Event) => {
      // Impede o mini-infobar nativo do Chrome; usamos nosso banner.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      recompute();
    };
    const onInstalled = () => {
      markInstalled(window.localStorage);
      setInstalled(true);
      setDeferredPrompt(null);
      // eslint-disable-next-line no-console
      console.info("[PWA] app instalado (appinstalled)");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [recompute]);

  const isIos = useMemo(() => ehIos(), []);
  const isStandalone = useMemo(() => estaInstalado(), []);

  const visible = shouldShowBanner({
    isMobile,
    isStandalone,
    installed,
    dismissActive: isDismissActive(window.localStorage, Date.now()),
    hasNativePrompt: deferredPrompt !== null,
    isIos,
  });

  const mode: InstallBannerMode = bannerMode(deferredPrompt !== null);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // eslint-disable-next-line no-console
    console.info(`[PWA] install prompt → ${choice.outcome}`);
    if (choice.outcome === "accepted") {
      markInstalled(window.localStorage);
      setInstalled(true);
    } else {
      // Recusou no prompt nativo → respeita os 7 dias.
      setDismissed(window.localStorage, Date.now());
    }
    setDeferredPrompt(null);
    recompute();
  }, [deferredPrompt, recompute]);

  const handleDismiss = useCallback(() => {
    setDismissed(window.localStorage, Date.now());
    // eslint-disable-next-line no-console
    console.info("[PWA] banner dispensado (7d)");
    recompute();
  }, [recompute]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Instalar aplicativo"
      data-testid="pwa-install-banner"
      data-mode={mode}
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 px-4 py-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:hidden"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Plus className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Instalar o SE_WEB</p>
          {mode === "native" ? (
            <p className="truncate text-xs text-muted-foreground">
              Adicione à tela inicial para acesso rápido.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Toque em{" "}
              <Share className="inline h-3 w-3 align-text-bottom" aria-label="Compartilhar" /> e
              depois em “Adicionar à Tela de Início”.
            </p>
          )}
        </div>
        {mode === "native" && (
          <Button size="sm" onClick={handleInstall} data-testid="pwa-install-cta">
            Adicionar
          </Button>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dispensar"
          data-testid="pwa-install-dismiss"
          className="ml-1 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default InstallBanner;
